import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import Header from "./Header";
import BottomNav from "./BottomNav";
import Footer from "./Footer";
import useCartStore, { cartCount } from "../../store/cartStore";
import useAuthStore from "../../store/authStore";
import useWishlistSync from "../../hooks/useWishlistSync";
import { useSettings } from "../../features/settings/api";

// Consistent with the splash's motion language: no bounce/elastic,
// a quiet, confident ease on every route change.
const pageTransition = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] },
};

export default function AppShell() {
  const location = useLocation();
  const items = useCartStore((s) => s.items);
  const authStatus = useAuthStore((s) => s.status);
  const checkAuth = useAuthStore((s) => s.checkAuth);
  useSettings(); // keeps lib/settingsCache.js fresh for formatPrice/buildWhatsAppLink

  // Storefront browsing never blocks on this (unlike AdminGuard, there's
  // no full-page spinner) — it just quietly resolves "is there already a
  // valid session cookie" so a returning customer's Account page and
  // wishlist sync work without them re-typing a password every visit.
  useEffect(() => {
    if (authStatus === "idle") checkAuth();
  }, [authStatus, checkAuth]);

  useWishlistSync();

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <Header />
      {/* Default desktop containment so pages that don't set their own
          max-width (most of them) don't stretch edge-to-edge on wide
          screens — pages needing something different (e.g. ProductDetail's
          two-column layout) simply define their own inner max-width. */}
      <main className="mx-auto w-full flex-1 pb-[calc(64px+env(safe-area-inset-bottom))] lg:max-w-5xl">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={pageTransition.initial}
            animate={pageTransition.animate}
            exit={pageTransition.exit}
            transition={pageTransition.transition}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
        <Footer />
      </main>
      <BottomNav cartCount={cartCount(items)} />
    </div>
  );
}
